import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Field, FormikProvider, useFormik } from 'formik'
import CustomInput from '../../../components/common/CustomInput'
import CustomSelect from '../../../components/common/CustomSelect'
import CustomFile from '../../../components/common/CustomFile'
import * as Yup from 'yup'
import axiosAuthInstance from '../../../utils/axios/axiosAuthInstance'
import axiosCommonInstance from '../../../utils/axios/axiosCommonInstance'
import "@google/model-viewer";
import Select from "react-select";
import toast from 'react-hot-toast';

// Validation
const validationSchema = Yup.object({
  company: Yup.string().trim().required('Company Name is required'),
  size: Yup.string().trim().required('Size is required'),
  product: Yup.string().trim().required('Product is required'),
  finish: Yup.string().trim().required('Finish is required'),
})


function Edit() {
  const navigate = useNavigate()
  const { id } = useParams()
  const [loading, setLoading] = useState(true)
  const [companyOptions, setCompanyOptions] = useState([])
  const [sizeOptions, setSizeOptions] = useState([])
  const [materialOptions, setmaterialOptions] = useState([])
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [productOptions, setProductOptions] = useState([]);
  const [colorOptions, setColorOptions] = useState([]);
  const [arView, setArView] = useState(null);
  const [materials, setMaterials] = useState([]);
  const [selectedMaterial, setSelectedMaterial] = useState(null);

  const formik = useFormik({
    initialValues: {
      company: '',
      size: '',
      product: '',
      finish: '',
      colors: [],
      image: '',
    },
    validationSchema: validationSchema,
    onSubmit: async (values, formikHelpers) => {
      const error = await formik.validateForm()
      if (Object.keys(error).length === 0) {
        handleSubmit(values, formikHelpers)
      } else {
        formikHelpers.setTouched(error)
      }
    },
  })

  const fetchCompanyData = async () => {
    try {
      const response = await axiosAuthInstance.get('company/dropdown')
      if (response && response.status === 200) {
        const transformedData = response?.data?.result?.map((item) => ({
          value: item._id,
          label: item.name,
        }))
        setCompanyOptions(transformedData)
      }
    } catch (error) {
      console.log('error', error)
    }
  }

  const fetchSizeData = async () => {
    try {
      const response = await axiosAuthInstance.get('size/dropdown')
      if (response.status === 200) {
        const transformedData = response?.data?.result?.map((item) => ({
          value: item._id,
          label: item.size,
        }))
        setSizeOptions(transformedData)
      }
    } catch (error) {
      console.log('error', error)
    }
  }

  const fetchMaterialData = async () => {
    try {
      const response = await axiosAuthInstance.get('material/dropdown')
      if (response && response.status === 200) {
        const transformedData = response?.data?.result?.map((item) => ({
          value: item._id,
          label: item.name,
        }))
        setmaterialOptions(transformedData)
      }
    } catch (error) {
      console.log('error', error)
    }
  }


  const fetchProductData = async (company, size, finish) => {
    try {
      const response = await axiosAuthInstance.get('product/dropdown', {
        company, size, finish
      });

      if (response.status === 200) {
        const transformedData = response?.data?.result?.map((item) => ({
          value: item._id,
          label: item.name,
        }));
        setProductOptions(transformedData);
      }
    } catch (error) {
      console.log('Error fetching finish data:', error);
    }
  };

  const fetchColorData = async () => {
    try {
      const response = await axiosAuthInstance.get('color/dropdown')
      if (response && response.status === 200) {
        const transformedData = response?.data?.result?.map((item) => ({
          value: item._id,
          label: item.name,
        }))
        setColorOptions(transformedData)
      }
    } catch (error) {
      console.log('error', error)
    }
  }

  useEffect(() => {
    fetchCompanyData()
    fetchSizeData()
    fetchMaterialData()
    fetchColorData()
  }, [])

  const fetchData = async () => {
    try {
      const response = await axiosCommonInstance.get(`arview/detail/${id}`)
      if (response.data && response.data.result) {
        const ARViewData = response.data.result

        formik.setValues({
          company: ARViewData.company._id,
          size: ARViewData.size._id,
          product: ARViewData?.product._id,
          finish: ARViewData.finish._id,
          colors: ARViewData?.colors?.map((item) => item._id),
          image: ARViewData.image,
        })
        setSelectedMaterial(Number(ARViewData?.selectedMaterial))
        setLoading(false)
      }
    } catch (error) {
      console.log('error in fetching edit data: ', error)
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [id])

  useEffect(() => {
    const { company, size, finish } = formik.values;
    if (company && size && finish) {
      fetchProductData(company, size, finish);
    }
  }, [formik.values.company, formik.values.size, formik.values.finish]);


  const handleSubmit = async (values, { resetForm }) => {
    setIsSubmitted(true)
    try {
      const formData = new FormData()
      formData.append('company', values.company);
      formData.append('size', values.size);
      formData.append('product', values.product);
      formData.append('colors', values.colors);
      formData.append('finish', values.finish);
      formData.append('selectedMaterial', selectedMaterial)

      if (selectedMaterial == null) {
        toast.error("Please select material to change color")
        setIsSubmitted(false)
        return;
      }

      const isBinaryFile = (file) => file instanceof File || file instanceof Blob;

      if (isBinaryFile(values.image)) {
        formData.append('image', values.image)
      }

      const response = await axiosAuthInstance.put(
        `arview/update/${id}`,
        formData,
      )
      if (response.status === 200) {
        resetForm()
        navigate('/arview')
      }
    } catch (error) {
      console.log(error)
    } finally {
      setIsSubmitted(false)
    }
  }

  useEffect(() => {
    const modelViewer = document.querySelector("model-viewer");
    if (modelViewer) {
      modelViewer.addEventListener("load", () => {
        if (modelViewer.model) {
          const materials = modelViewer.model.materials.map((mat, index) => ({
            label: mat.name || `Material ${index + 1}`,
            value: index,
          }));
          setMaterials(materials);

          // Automatically select the first material if there is only one
          if (materials.length === 1) {
            setSelectedMaterial(materials[0].value); // Set the first material
          }
        } else {
          console.error("Model materials not found.");
        }
      });
    }
  }, [formik.values.image]);

  useEffect(() => {
    if (formik.values.image) {
      const modelViewer = document.querySelector("model-viewer");
      if (modelViewer && modelViewer.model) {
        modelViewer.model.materials.forEach((material, index) => {
          if (selectedMaterial == index) {
            if (material && material.pbrMetallicRoughness) {
              material.pbrMetallicRoughness.setBaseColorFactor('#bae6fd'); // Set the selected color
            } else {
              console.warn(`Material ${index} does not support PBR.`);
            }
          } else {
            // Reset to default color for non-selected materials
            if (material && material.pbrMetallicRoughness) {
              material.pbrMetallicRoughness.setBaseColorFactor("#fff");
            }
          }
        });
      }
    }
  }, [selectedMaterial]);

  const handleMaterialSelection = (selectedOption) => {
    const selectedIndex = selectedOption ? selectedOption.value : null;
    setSelectedMaterial(selectedIndex);
  };

  if (loading) {
    return <div>Loading...</div>
  }

  return (
    <FormikProvider value={formik}>
      <form className="flex flex-col gap-6 p-4" onSubmit={formik.handleSubmit}>
        <div className='flex gap-4'>
          <Field
            name="company"
            label="Company Name"
            placeholder="select company "
            required
            component={CustomSelect}
            options={companyOptions}
          />
          <Field
            name="size"
            label="Size"
            placeholder="Select Size"
            required
            component={CustomSelect}
            options={sizeOptions}
          />
        </div>
        <div className='flex gap-4'>
          <Field
            name="finish"
            label="Finish"
            placeholder="Select Finish"
            required
            component={CustomSelect}
            options={materialOptions}
          />
          <Field
            name="product"
            label="Select Product"
            placeholder="Select Product"
            required
            component={CustomSelect}
            options={productOptions}
          />
        </div>
        <div className='flex gap-4'>
          <Field
            name="colors"
            label="Select colors"
            placeholder="Select colors"
            component={CustomSelect}
            options={colorOptions}
            isMulti
          />

          <Field
            name="image"
            label="AR Model"
            placeholder="Upload AR Model"
            component={CustomFile}
            onChange={(event) => {
              const file = event.currentTarget.files[0];
              formik.setValues({ ...formik.values, "image": file });
              setArView(file ? URL.createObjectURL(file) : "");
            }}
          />
        </div>

        {
          formik.values.image &&
          <div className='flex gap-4'>
            <div className='w-1/2 h-72 shadow-md bg-white'>
              <model-viewer
                src={formik.values.image}
                alt="3D model"
                auto-rotate
                camera-controls
                ar
                ar-modes="webxr scene-viewer quick-look"
                style={{ width: "100%", height: "100%" }}
              ></model-viewer>
            </div>

            <div className='w-72'>
              <Select
                options={materials}
                onChange={handleMaterialSelection}
                value={materials.find((option) => option.value == selectedMaterial)}
                placeholder="Select Material to change color"
              />
            </div>
          </div>
        }

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={isSubmitted}
            className={`bg-orange-500 text-white px-6 py-2 rounded-lg text-lg font-medium ${isSubmitted ? 'opacity-50 cursor-not-allowed' : 'hover:bg-orange-600'
              }`}
          >
            {isSubmitted ? (
              <div className="w-5 h-5 mx-auto border-2 border-white border-solid rounded-full loader animate-spin border-t-transparent"></div>
            ) : (
              'Submit'
            )}
          </button>
        </div>
      </form>
    </FormikProvider>
  )
}

export default Edit
